// Sales Dashboard report — counts + $ of sales orders / POs / in production /
// invoices in the selected window. Click a tile to list every row behind it
// (Stephanie 2026-10-16: "i want to see the orders not just the numbers").
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { ReportShell, useReportRange } from "@/components/ReportShell";
import { ReportRowsTable, ReportTile, type ReportList } from "@/components/ReportRowsTable";

interface Metric {
  count: number;
  total: string;
}

interface Report {
  range: { from: string; to: string };
  salesOrders: Metric;
  /** null for the Marketing role — the PO tile is supplier cost (CEO 2026-10-21). */
  pos: Metric | null;
  bookedOrders: Metric;
  invoices: Metric;
  fetchedAt: string;
}

const TILES: Array<{ list: ReportList; key: keyof Omit<Report, "range" | "fetchedAt">; label: string; sub: string; amountLabel: string; title: string }> = [
  { list: "sales_orders", key: "salesOrders", label: "Sales orders", sub: "Orders past the estimate stage, created in the range", amountLabel: "Order total", title: "Sales orders created" },
  { list: "pos", key: "pos", label: "Purchase orders", sub: "POs created in the range", amountLabel: "PO cost", title: "Purchase orders created" },
  { list: "booked", key: "bookedOrders", label: "In production", sub: "Orders in production right now (any date)", amountLabel: "Order total", title: "Orders in production now" },
  { list: "invoices", key: "invoices", label: "Invoiced", sub: "Invoices created in the range", amountLabel: "Invoiced", title: "Invoices created" },
];

export function ReportSalesDashboardPage() {
  const { preset, setPreset, range, setCustom, repId, setRepId } = useReportRange();
  const [list, setList] = useState<ReportList>("sales_orders");
  const rep = repId ? `&rep_id=${repId}` : "";

  const { data, isLoading, isError } = useQuery({
    queryKey: ["reports", "sales-dashboard", range.from, range.to, repId],
    queryFn: () => api<Report>(`/reports/sales-dashboard?from=${range.from}&to=${range.to}${rep}`),
  });
  const tile = TILES.find((t) => t.list === list) ?? TILES[0];

  return (
    <ReportShell
      title="Sales Dashboard"
      subtitle="What got sold, ordered from suppliers and invoiced in the range. Click a box to see every order behind it."
      preset={preset}
      onPresetChange={setPreset}
      range={range}
      onCustomRange={setCustom}
      repId={repId}
      onRepChange={setRepId}
      fetchedAt={data?.fetchedAt}
    >
      {isLoading ? (
        <div className="rounded-lg border border-brown-100 bg-white p-10 text-center text-sm text-brown-300">Loading…</div>
      ) : isError || !data ? (
        <div className="rounded-lg border border-rust-300 bg-rust-50 p-10 text-center text-sm text-rust-600">Couldn't load report.</div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {TILES.filter((t) => data[t.key] != null).map((t) => (
            <ReportTile
              key={t.list}
              label={t.label}
              value={data[t.key]!.count}
              amount={data[t.key]!.total}
              amountLabel={t.amountLabel}
              sub={t.sub}
              selected={list === t.list}
              onSelect={() => setList(t.list)}
            />
          ))}
        </div>
      )}
      <ReportRowsTable list={list} range={range} repId={repId} title={tile?.title ?? ""} />
    </ReportShell>
  );
}
